import React from "react";
import ServiceRubricaLote from "@/services/ServiceRubricaLote";
import { notify } from "@/stores/useToast";
import { mensagemErro } from "@/utils/apiError";
import { Rubrica } from "@/types/rubricas/Rubrica";
import type {
  PayloadAcaoEmMassa,
  ProcessamentoLoteRequest,
  ProcessamentoLoteResponse,
} from "@/types/rubricas/analise-lote/ProcessamentoLote";
import { gerarChaveIdempotencia } from "@/components/rubricas/processamentoLote";
import {
  EDICAO_INICIAL,
  type EdicaoAcaoEmMassa,
  camposAlterados,
  chaveNatureza,
  incidenciasRecomendadas,
  montarPayload,
  pendenciasEdicao,
  removerNaturezas,
  requisicaoAcaoEmMassa,
} from "@/pages/folha-pagamento/acao-em-massa/acaoEmMassa";
import { EtapaCamposAfetados } from "@/pages/folha-pagamento/acao-em-massa/EtapaCamposAfetados";
import { EtapaRelacaoRubricas } from "@/pages/folha-pagamento/acao-em-massa/EtapaRelacaoRubricas";
import { EtapaRevisao } from "@/pages/folha-pagamento/acao-em-massa/EtapaRevisao";
import { StepperAcaoEmMassa } from "@/pages/folha-pagamento/acao-em-massa/StepperAcaoEmMassa";
import { useCatalogosAcaoEmMassa } from "@/pages/folha-pagamento/acao-em-massa/useCatalogosAcaoEmMassa";
import { useNomesEmpregadores } from "@/pages/folha-pagamento/acao-em-massa/useNomesEmpregadores";
import { usePreviaProcessamento } from "@/pages/folha-pagamento/acao-em-massa/usePreviaProcessamento";

/** O que foi salvo na etapa 2 — a revisão mostra exatamente o que foi enviado. */
interface Revisao {
  rubricas: Rubrica[];
  payload: PayloadAcaoEmMassa;
  campos: string[];
  removidas: { rubricas: number; naturezas: string[] };
  lote: ProcessamentoLoteResponse;
}

export interface WizardAcaoEmMassaProps {
  rubricas: Rubrica[];
  origem: string;
  onCancelar: () => void;
  /** Chamado depois que o lote foi aceito para processamento. */
  onConcluir: () => void;
}

/**
 * Etapas: 1) escolher as rubricas; 2) definir os campos — "Salvar e avançar" cria a ACAO_EM_MASSA e o
 * worker prepara a prévia (com o grau de risco que será aplicado); 3) revisar e confirmar
 * (/confirmar). Sem campos alterados o payload vai vazio: o worker só recalcula e aprova.
 */
export function WizardAcaoEmMassa({
  rubricas,
  origem,
  onCancelar,
  onConcluir,
}: WizardAcaoEmMassaProps) {
  const [etapa, setEtapa] = React.useState(0);
  const [incluidas, setIncluidas] = React.useState<ReadonlySet<number>>(
    () => new Set(rubricas.map((rubrica) => rubrica.idAnaliseRubrica)),
  );
  const [edicao, setEdicao] = React.useState<EdicaoAcaoEmMassa>(EDICAO_INICIAL);
  const [revisao, setRevisao] = React.useState<Revisao | null>(null);
  const [salvando, setSalvando] = React.useState(false);
  const [confirmando, setConfirmando] = React.useState(false);
  // Mesmo corpo → mesma Idempotency-Key: voltar e salvar de novo sem mudar nada reaproveita o lote.
  const chaves = React.useRef(new Map<string, string>());

  const catalogos = useCatalogosAcaoEmMassa();
  const nomeEmpregador = useNomesEmpregadores();
  const { estado: previa, recarregar: recarregarPrevia } = usePreviaProcessamento(
    etapa === 2 ? (revisao?.lote.idProcessamentoLote ?? null) : null,
  );

  const selecionadas = React.useMemo(
    () => rubricas.filter((rubrica) => incluidas.has(rubrica.idAnaliseRubrica)),
    [incluidas, rubricas],
  );
  const efetivas = React.useMemo(
    () => removerNaturezas(selecionadas, edicao.naturezasRemovidas),
    [edicao.naturezasRemovidas, selecionadas],
  );
  const recomendadas = React.useMemo(
    () => incidenciasRecomendadas(efetivas, edicao, catalogos.naturezas),
    [catalogos.naturezas, edicao, efetivas],
  );
  const payload = React.useMemo(() => montarPayload(edicao, recomendadas), [edicao, recomendadas]);

  const removidas = React.useMemo(() => {
    const efetivasIds = new Set(efetivas.map((rubrica) => rubrica.idAnaliseRubrica));
    const fora = selecionadas.filter((rubrica) => !efetivasIds.has(rubrica.idAnaliseRubrica));
    return {
      rubricas: fora.length,
      naturezas: [...new Set(fora.map((r) => String(r.naturezaCodigo ?? "—")))],
    };
  }, [efetivas, selecionadas]);

  // Naturezas removidas que saíram da seleção na etapa 1 não devem continuar valendo.
  React.useEffect(() => {
    const existentes = new Set(selecionadas.map(chaveNatureza));
    setEdicao((atual) => {
      const naturezasRemovidas = atual.naturezasRemovidas.filter((chave) => existentes.has(chave));
      return naturezasRemovidas.length === atual.naturezasRemovidas.length
        ? atual
        : { ...atual, naturezasRemovidas };
    });
  }, [selecionadas]);

  const criar = (corpo: ProcessamentoLoteRequest) => {
    const assinatura = JSON.stringify(corpo);
    const chave = chaves.current.get(assinatura) ?? gerarChaveIdempotencia();
    chaves.current.set(assinatura, chave);
    return ServiceRubricaLote.criarProcessamento(corpo, chave);
  };

  const irPara = (proxima: number) => {
    setEtapa(proxima);
    window.scrollTo?.({ top: 0 });
  };

  const salvarCampos = async () => {
    const base = {
      rubricas: efetivas,
      payload,
      campos: camposAlterados(payload, edicao),
      removidas,
    };
    setSalvando(true);
    try {
      const { data } = await criar(requisicaoAcaoEmMassa(efetivas, payload, edicao.comentario));
      setRevisao({ ...base, lote: data });
      irPara(2);
    } catch (err) {
      notify.error(mensagemErro(err, "Não foi possível salvar as alterações da ação em massa."));
    } finally {
      setSalvando(false);
    }
  };

  const confirmar = async () => {
    if (!revisao) return;
    setConfirmando(true);
    try {
      await ServiceRubricaLote.confirmarProcessamento(revisao.lote.idProcessamentoLote);
      const total = revisao.rubricas.length;
      notify.success(
        revisao.campos.length
          ? "Alterações confirmadas e enviadas para processamento."
          : `Aprovação de ${total.toLocaleString("pt-BR")} ${total === 1 ? "rubrica" : "rubricas"} enviada para processamento.`,
      );
      setConfirmando(false);
      onConcluir();
    } catch (err) {
      notify.error(mensagemErro(err, "Não foi possível confirmar as alterações."));
      setConfirmando(false);
    }
  };

  return (
    <div data-qa="acao-massa-wizard">
      <StepperAcaoEmMassa atual={etapa} />

      {etapa === 0 && (
        <EtapaRelacaoRubricas
          rubricas={rubricas}
          incluidas={incluidas}
          onIncluidasChange={setIncluidas}
          origem={origem}
          nomeEmpregador={nomeEmpregador}
          onCancelar={onCancelar}
          onAvancar={() => irPara(1)}
        />
      )}

      {etapa === 1 && (
        <EtapaCamposAfetados
          origem={origem}
          rubricas={selecionadas}
          efetivas={efetivas.length}
          edicao={edicao}
          onEdicaoChange={setEdicao}
          catalogos={catalogos}
          recomendadas={recomendadas}
          pendencias={pendenciasEdicao(edicao)}
          salvando={salvando}
          onVoltar={() => irPara(0)}
          onSalvar={salvarCampos}
        />
      )}

      {etapa === 2 && revisao && (
        <EtapaRevisao
          origem={origem}
          rubricas={revisao.rubricas}
          payload={revisao.payload}
          campos={revisao.campos}
          removidas={revisao.removidas}
          previa={previa}
          nomeEmpregador={nomeEmpregador}
          confirmando={confirmando}
          onAtualizarPrevia={recarregarPrevia}
          onVoltar={() => irPara(1)}
          onConfirmar={confirmar}
        />
      )}
    </div>
  );
}
